import api from '../utils/api';

const BASE = '/api/auth';

/**
 * Register a new account. Triggers a verification email server-side.
 * Requires a Turnstile token; a missing or invalid one rejects with 403 and
 * `captchaRequired: true`.
 * @route POST /api/auth/register
 * @param {{ email: string, username: string, password: string, passwordVerify: string, inviteToken?: string|null, captchaToken?: string, website?: string }} data
 * @returns {Promise<{ message: string }>}
 */
export const register = (data) =>
    api.post(`${BASE}/register`, data).then((r) => r.data);

/**
 * Log in with a username/email and password. Sets the auth cookie on success.
 * When the server judges a login risky it rejects with 403 and
 * `captchaRequired: true`; retry with a Turnstile `captchaToken`.
 * @route POST /api/auth/login
 * `website` is the hidden honeypot field; anything in it also triggers the CAPTCHA.
 * @param {{ userIdentifier: string, password: string, captchaToken?: string, website?: string }} credentials
 * @returns {Promise<{ message: string, user: Object }>}
 */
export const login = (credentials) =>
    api.post(`${BASE}/login`, credentials).then((r) => r.data);

/**
 * Sign in/up with a Google ID token. Sets the auth cookie on success.
 * @route POST /api/auth/google
 * @param {{ idToken: string, inviteToken?: string|null }} data
 * @returns {Promise<{ message: string, user: Object }>}
 */
export const googleSignIn = (data) =>
    api.post(`${BASE}/google`, data).then((r) => r.data);

/**
 * Clear the auth cookie / log the user out.
 * @route GET /api/auth/logout
 * @returns {Promise<{ message: string }>}
 */
export const logout = () =>
    api.get(`${BASE}/logout`).then((r) => r.data);

/**
 * Check whether the current cookie corresponds to a logged-in user.
 * @route GET /api/auth/loggedIn
 * @returns {Promise<{ loggedIn: boolean, user?: Object }>}
 */
export const loggedIn = () =>
    api.get(`${BASE}/loggedIn`).then((r) => r.data);

/**
 * Request a fresh verification email for an unverified account.
 * @route POST /api/auth/resend-verification
 * @param {string} email
 * @returns {Promise<{ message: string }>}
 */
export const resendVerification = (email) =>
    api.post(`${BASE}/resend-verification`, { email }).then((r) => r.data);

/**
 * Request a password reset email.
 * @route POST /api/auth/forgot-password
 * @param {string} email
 * @returns {Promise<{ message: string }>}
 */
export const forgotPassword = (email) =>
    api.post(`${BASE}/forgot-password`, { email }).then((r) => r.data);

/**
 * Reset a password using a reset token from the emailed link.
 * @route POST /api/auth/reset-password
 * @param {{ token: string, password: string, passwordConfirm: string }} data
 * @returns {Promise<{ message: string }>}
 */
export const resetPassword = (data) =>
    api.post(`${BASE}/reset-password`, data).then((r) => r.data);

/**
 * Change the logged-in user's password.
 * @route POST /api/auth/change-password
 * @param {{ currentPassword: string, newPassword: string, newPasswordConfirm: string }} data
 * @returns {Promise<{ successMessage: string }>}
 */
export const changePassword = (data) =>
    api.post(`${BASE}/change-password`, data).then((r) => r.data);
